/**
 * Responsibility: choose which grade's sales are being looked at.
 *
 * A row of chips rather than lines on one chart. A PSA 10 Charizard sells
 * around $12,000 and a raw one around $450; drawn together, the raw series
 * is a flat line along the bottom and the axis belongs to neither. One
 * grade at a time is the only way both are readable.
 *
 * Counts are on the chips because they are the warning. A grade with four
 * sales behind it has a price in the same sense a rumour has a source, and
 * that is worth seeing before the chart is believed.
 */
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import type { SaleBucket } from './saleGrades';

type GradePickerProps = {
  buckets: SaleBucket[];
  value: string;
  onChange: (next: string) => void;
};

export function GradePicker({ buckets, value, onChange }: GradePickerProps) {
  // One bucket is not a choice, and a lone chip only looks like a filter
  // that might be hiding something.
  if (buckets.length < 2) return null;

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ flexShrink: 0 }}>
      {buckets.map((bucket) => (
        <Chip
          key={bucket.key}
          size="small"
          clickable
          label={`${bucket.label} (${bucket.count})`}
          color={bucket.key === value ? 'primary' : 'default'}
          variant={bucket.key === value ? 'filled' : 'outlined'}
          onClick={() => onChange(bucket.key)}
        />
      ))}
    </Stack>
  );
}
